// Journal — historique des consommations, triable et filtrable par période.
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { EmptyState, PeriodPicker } from '@/components/ui';
import { consumptionNutrients, filterByPeriod, MEAL_SHORT_LABELS, type Period } from '@/domain';
import { useConsumptions, useDeleteConsumption } from '@/hooks/queries';
import { confirmDelete } from '@/lib/confirm';

type SortKey = 'date' | 'kcal';
type SortDir = 'asc' | 'desc';

export function JournalPage() {
  const { data: consumptions = [] } = useConsumptions();
  const deleteC = useDeleteConsumption();

  const [period, setPeriod] = useState<Period>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => {
    const scoped = filterByPeriod(consumptions, (c) => c.datetime, period).map((c) => ({
      ...c,
      kcal: Math.round(consumptionNutrients(c).kcal),
    }));
    const sign = sortDir === 'asc' ? 1 : -1;
    scoped.sort((a, b) => {
      const cmp = sortKey === 'date' ? a.datetime.localeCompare(b.datetime) : a.kcal - b.kcal;
      return cmp * sign;
    });
    return scoped;
  }, [consumptions, period, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Journal</h1>
          <p>Historique de toutes tes consommations ({rows.length}).</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {rows.length === 0 ? (
        <EmptyState>Aucune consommation sur cette période.</EmptyState>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th
                    aria-sort={
                      sortKey === 'date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                  >
                    <button type="button" className="th-sort" onClick={() => toggleSort('date')}>
                      Date{arrow('date')}
                    </button>
                  </th>
                  <th>Aliment</th>
                  <th>Quantité</th>
                  <th>Repas</th>
                  <th
                    aria-sort={
                      sortKey === 'kcal' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                  >
                    <button type="button" className="th-sort" onClick={() => toggleSort('kcal')}>
                      Calories{arrow('kcal')}
                    </button>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>{format(parseISO(c.datetime), 'd MMM yyyy, HH:mm', { locale: fr })}</td>
                    <td>{c.foodName || '—'}</td>
                    <td>{c.grams} g</td>
                    <td>{c.meal ? MEAL_SHORT_LABELS[c.meal] : '—'}</td>
                    <td>{c.kcal} kcal</td>
                    <td>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => {
                          if (confirmDelete('cette consommation')) deleteC.mutate(c.id);
                        }}
                      >
                        Suppr.
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
